import { useMemo } from 'react';
import { ApplicantPaymentDisplay, CurrencyDisplay } from '@aidkitorg/component-library';
import { PotentialCandidate } from '@aidkitorg/aidkit/lib/application/multiplex';
import { ServedPortalPayments, RichText } from '@aidkitorg/types/lib/survey';
import {
  AppInfo,
  deriveIssuedPayment,
  PortalIssuedPayment,
  totalPaidInCents,
} from '@aidkitorg/types/lib/payments/portalPayments';
import { useLocalizedStrings } from '../Localization';
import { useModularMarkdown } from '../Hooks/ModularMarkdown';
import { MARKDOWN_NO_MARGIN_CLASSES } from '../ApplicantPortalPage';
import { getPaymentName, paymentTypeToString } from '../utils/payment_utils';

type PaymentRow = {
  payment: ServedPortalPayments['payments'][number],
  state: PortalIssuedPayment,
};

// Use the same payment-method labels the staff payment page shows (ACH,
// USIO Card Load, ...) so applicants and staff see consistent wording.
const methodLabel = (paymentType: string | undefined): string | undefined =>
  paymentType ? paymentTypeToString(paymentType) : undefined;

// AidKit programs pay US dollars, so amounts use US conventions ($1,500.00)
// for every applicant regardless of UI language — currency formatting is a
// regional convention, not a linguistic one, and keying off the browser
// language would give a Spanish speaker "1.500,00 US$" instead.
const PAYMENT_LOCALE = 'en-US';

function MarkdownBlock(props: { content: RichText, info: AppInfo, className?: string }) {
  const marked = useModularMarkdown({
    content: props.content,
    info: { ...props.info },
  });
  return <div className={`${props.className || ''} ${MARKDOWN_NO_MARGIN_CLASSES}`}>{marked}</div>;
}

export function PortalPaymentsComponent(props: {
  component: ServedPortalPayments,
  candidate: PotentialCandidate,
  Viewer: 'screener' | 'applicant',
}) {
  const L = useLocalizedStrings();
  const P = L.applicant.portal_payments;
  const info: AppInfo = props.candidate.info || {};
  const { component } = props;

  const rows: PaymentRow[] = useMemo(() => (component.payments || [])
    .flatMap(payment => {
      const state = deriveIssuedPayment(info, payment.targetField);
      return state ? [{ payment, state }] : [];
    })
    // Newest first, like a statement.
    .sort((a, b) => (b.state.issuedDate || '').localeCompare(a.state.issuedDate || '')),
  [component, info]);

  const totalPaid = totalPaidInCents(rows.map(r => r.state));
  // Only programs that maintain a current-method field get this block —
  // inferring it from the latest payment would turn a historic fact into a
  // promise about future payments.
  const currentMethod = methodLabel(info[component.currentMethodField || 'applicant_payment_type']);

  // An application with no issued payments gets just the empty message — a
  // "$0.00 total paid" summary reads as a missing payment, not an absent one.
  if (!rows.length) {
    return (
      <div className="mb-4 flex flex-col gap-3">
        {component.header && <MarkdownBlock content={component.header} info={info} className="text-sm" />}
        {component.emptyMessage
          ? <MarkdownBlock content={component.emptyMessage} info={info} className="text-sm text-slate-500" />
          : <p className="text-sm text-slate-500 mb-0">{P.no_payments}</p>}
      </div>
    );
  }

  return (
    <div className="mb-4 flex flex-col gap-3">
      {component.header && <MarkdownBlock content={component.header} info={info} className="text-sm" />}

      <div className="w-full py-4 flex flex-col gap-3 sm:flex-row justify-between sm:items-end border-b-4 border-indigo-600">
        <div className="flex flex-col">
          <span className="text-sm text-slate-500 uppercase tracking-wide">{P.total_amount_paid}</span>
          <CurrencyDisplay className="font-semibold text-3xl text-slate-900" value={totalPaid / 100} currency="USD" locale={PAYMENT_LOCALE} />
        </div>
        {currentMethod && (
          <div className="flex flex-col sm:items-end">
            <span className="text-sm text-slate-500 uppercase tracking-wide">{P.current_payment_method}</span>
            <span className="font-semibold text-xl text-slate-900">{currentMethod}</span>
          </div>
        )}
      </div>

      <h4 className="text-base font-semibold text-slate-900 mt-2 mb-0">{P.payment_history}</h4>
      <div className="flex flex-col gap-3">
        {rows.map(row => (
          <ApplicantPaymentDisplay
            key={row.payment.targetField}
            paymentName={getPaymentName(row.payment.name, row.payment.targetField, 1, L)}
            method={methodLabel(row.state.paymentType)}
            amountInCents={row.state.amountInCents}
            issuedDate={row.state.issuedDate ? new Date(row.state.issuedDate) : undefined}
            traceId={row.state.traceId}
          />
        ))}
      </div>
    </div>
  );
}
